import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { ConfirmModal } from './ConfirmModal';
import { suspendUserSchema, type SuspendUserInput } from '../schemas';
import { useSuspendUser } from '../hooks';

interface Props {
  open: boolean;
  userId: string;
  userName?: string;
  onClose: () => void;
  onSuccess?: () => void;
}

export function SuspendUserModal({ open, userId, userName, onClose, onSuccess }: Props) {
  const mutation = useSuspendUser();
  const form = useForm<SuspendUserInput>({
    resolver: zodResolver(suspendUserSchema),
    defaultValues: { reason: '' },
  });

  const submit = form.handleSubmit(async (values) => {
    await mutation.mutateAsync({ id: userId, reason: values.reason });
    form.reset();
    onSuccess?.();
    onClose();
  });

  return (
    <ConfirmModal
      open={open}
      title={`Suspend ${userName ?? 'user'}`}
      description="The user will be blocked, their sessions revoked, and notified by email."
      confirmLabel="Suspend"
      destructive
      loading={mutation.isPending}
      onClose={onClose}
      onConfirm={() => void submit()}
      disableConfirm={!form.formState.isValid && form.formState.isSubmitted}
    >
      <form onSubmit={submit} className="space-y-2">
        <label className="block text-[10px] uppercase tracking-widest font-bold text-on-surface-variant">
          Reason
        </label>
        <textarea
          {...form.register('reason')}
          rows={3}
          className="w-full px-3 py-2 rounded-lg bg-surface-container-lowest border border-border-subtle text-sm focus:outline-none focus:ring-2 focus:ring-red-500/30"
          placeholder="Describe why this user is being suspended (min 10 chars)"
        />
        {form.formState.errors.reason && (
          <p className="text-xs text-red-300">{form.formState.errors.reason.message}</p>
        )}
        {mutation.error && (
          <p className="text-xs text-red-300">{(mutation.error as Error).message}</p>
        )}
      </form>
    </ConfirmModal>
  );
}
